// Scene layers on the page (issue #38): a few things in a scene are switches that add a part to the music (a scene's
// spots of a layer kind, site/js/ui/spots.js; the parts: site/js/audio/layers.js). In Paris the turntable adds a shaker,
// the desk lamp a warm pad and the painting twinkling notes. A tap on the thing flips its switch (app.js tapSpot), and
// so does its switch in the Vibe panel, one each in a row under "In this scene" at the panel's foot: the way in for a
// keyboard or a screen reader, and where a visitor reads what the things are (the line under them, and each one's
// own description). The part joins on the track's next bar line and stops on the next one; until then the switch
// waits, which the thing shows (a pale note over it, its light growing to the bar line: spots.js drawLayers) and the
// panel says. The player holds the truth (player.layers, its 'layers' event); this keeps what the page shows. Layers
// belong to the scene on screen: leaving it switches them off, and every visit starts without any. None in OBS or
// ?creator, nor on the radio (the same song for everyone).
//   layerLine(mark, does, playing) · layerSay(name, does, mark, playing) · layersLine(marks, playing, verb)
//   (pure: tests/layers.test.mjs)
//   createLayerUi({ form, player, … }) → { scene(inst), tap(spot), marks() }
import { KINDS, layerOf, layerSpots } from './spots.js';

/** A switch's description: what the thing adds, or where its switch is on the way to. `mark`: { want, on, fits,
 *  hit }; `playing`: the music plays (else nothing can join yet). */
export function layerLine(mark, does, playing = true) {
  if (mark.want && !mark.on) return playing && mark.hit != null ? `${does}, from the next bar` : `${does}, once the music plays`;
  if (!mark.want && mark.on) return 'off from the next bar';
  if (!mark.want) return `adds ${does}`;
  return mark.fits === false ? `${does}, resting: this track has no beat` : `${does}, playing along`;
}
/** What a tap on the thing says in a toast, after the switch was flipped to mark.want. */
export function layerSay(name, does, mark, playing = true) {
  if (mark.want) return mark.on ? `${name}: ${does}, staying on` : `${name}: ${does}, ${playing && mark.hit != null ? 'from the next bar' : 'once the music plays'}`;
  return mark.on ? `${name}: off from the next bar` : `${name}: staying off`;
}
/** The line under the switches: every thing that is on or on its way, or, with none, how they work. `marks`:
 *  [{ name, does, want, on, fits, hit }]; `verb`: what a visitor does to a thing in the scene ('tap' or 'click'). */
export function layersLine(marks, playing = true, verb = 'tap') {
  const busy = marks.filter(m => m.want || m.on);
  if (!busy.length) return `Or ${verb} one in the scene · joins on a bar line`;
  return busy.map(m => `${m.name}: ${layerLine(m, m.does, playing)}`).join(' · ');
}

/**
 * form: the Vibe panel (its switches go at its foot); player(): the audio player, or the page's
 * silent stand-in while it loads; radio(): whether the radio is on; off: no layers on this page (OBS, ?creator);
 * still(): prefers-reduced-motion; playing(): the music plays; toast, track: the page's own; touch: a finger taps.
 * → scene(inst): the scene on screen is now `inst` ({ id, look, scene }): the layers of the one before go off, and the
 *     switches are this one's;
 *   tap(spot): a tap on a layer's spot: flips it, says so, returns true (false where there are no layers);
 *   marks(): what drawLayers draws: the layers switched on or still sounding, [{ spot, want, on, since, hit, still }].
 */
export function createLayerUi({ form, player, radio = () => false, off = false, still = () => false, playing = () => true, toast = () => {}, track = () => {}, touch = false, now = () => performance.now() }) {
  const group = document.createElement('fieldset'), rows = new Map(), marks = new Map(), shown = [];
  let at = null, heardFrom = null;
  group.id = 'sceneLayers';
  group.hidden = true;
  // a row of switches (the Band's look: `.seg`), each one's description out of sight for a screen reader, and a line
  // under them for everyone
  group.innerHTML = '<legend>In this scene</legend><div class="seg" role="group" aria-label="Things in the scene that add a part to the music"></div>'
    + '<div class="layer-notes"></div><p class="vibe-note" role="status"></p>';
  const list = group.querySelector('.seg'), notes = group.querySelector('.layer-notes'), line = group.querySelector('.vibe-note');
  form.append(group);                                 // the panel's foot, under the note on style, energy and band

  const does = id => KINDS[marks.get(id).spot.kind].does;
  function render() {
    shown.length = 0;
    for (const [id, m] of marks) {
      if (m.want || m.on) shown.push(m);
      const row = rows.get(id);
      if (!row) continue;
      row.input.checked = m.want;
      row.note.textContent = layerLine(m, does(id), playing());
      row.label.classList.toggle('waiting', m.want !== m.on);
    }
    line.textContent = layersLine([...marks].map(([id, m]) => ({ ...m, name: m.spot.name, does: does(id) })), playing(), touch ? 'tap' : 'click');
  }
  /** the player's word on every layer: what sounds, and when the ones waiting change */
  function heard(states) {
    for (const [id, m] of marks) {
      const st = states && states[id];
      if (!st) continue;
      Object.assign(m, { want: st.want, on: st.on, fits: st.fits, hit: st.in != null ? now() + st.in * 1000 : null });
    }
    render();
  }
  /** the player, once it is the real one: hear its layers, and tell it the ones switched on while it loaded */
  function attach() {
    const a = player();
    if (a === heardFrom || typeof a.setLayer !== 'function') return a;
    heardFrom = a;
    a.on('layers', heard);
    a.on('state', () => heard(a.layers));           // paused, playing again: the bar line is elsewhere on the page's clock
    for (const [id, m] of marks) if (m.want) a.setLayer(id, true);
    heard(a.layers);
    return a;
  }
  const props = (id, m) => ({ city: at.id, look: at.look, layer: id, thing: m.spot.name, music_playing: playing() });

  function flip(id, want, trigger) {
    const m = marks.get(id);
    if (!m || want === m.want) return;
    const a = attach(), name = m.spot.name;
    // not on the radio (the same song for everyone), nor over a playlist's files (no bars to join): the player says no
    const no = want && radio() ? 'The radio plays the same song for everyone · layers join your own mix'
      : (want && a.track && a.track.source !== 'generative') || (typeof a.setLayer === 'function' && a.setLayer(id, want) === null)
        ? 'Layers play along with the generated music only' : null;
    if (no) {
      toast(no, 2600);
      render();                                       // its switch goes back
      return;
    }
    Object.assign(m, { want, since: now(), still: still() });
    if (want) m.from = now();
    if (typeof a.setLayer === 'function') heard(a.layers); else render();
    if (trigger === 'scene') toast(layerSay(name, does(id), m, playing()), 1800);   // the panel's switch shows it there
    track('layer_toggled', {
      ...props(id, m), on: want, trigger, layers_on: [...marks.values()].filter(x => x.want).length, style: a.track?.style ?? null,
      ...(want ? {} : { seconds: Math.round((now() - (m.from ?? now())) / 1000) }),
    });
  }

  return {
    scene(inst) {
      const a = attach();
      for (const [id, m] of marks) {
        if (!m.want && !m.on) continue;
        const wanted = m.want;                        // (the player's word on it, heard as it is told, takes it back)
        if (typeof a.setLayer === 'function') a.setLayer(id, false);
        if (wanted) track('layer_toggled', { ...props(id, m), on: false, trigger: 'leave', layers_on: 0, style: a.track?.style ?? null, seconds: Math.round((now() - (m.from ?? now())) / 1000) });
      }
      marks.clear(); rows.clear(); shown.length = 0;
      list.replaceChildren(); notes.replaceChildren();
      at = inst ? { id: inst.id, look: inst.look } : null;
      for (const spot of off || !inst ? [] : layerSpots(inst.scene)) {
        const id = layerOf(spot), label = document.createElement('label');
        marks.set(id, { spot, want: false, on: false, fits: true, since: 0, hit: null, still: false });
        const note = Object.assign(document.createElement('span'), { id: `layerNote-${id}` });
        label.innerHTML = `<input type="checkbox" role="switch" name="layer" value="${id}" aria-describedby="layerNote-${id}"><span></span>`;
        label.title = `${spot.name}: adds ${KINDS[spot.kind].does}`;
        label.lastChild.textContent = spot.name;
        const input = label.firstChild;
        // (the form's own change handler takes every other input for a vibe setting)
        input.addEventListener('change', e => { e.stopPropagation(); flip(id, input.checked, 'switch'); });
        rows.set(id, { label, input, note });
        list.append(label); notes.append(note);
      }
      group.hidden = !marks.size;
      render();
    },
    tap(spot) {
      const id = layerOf(spot), m = marks.get(id);
      if (off || !m) return false;
      flip(id, !m.want, 'scene');
      return true;
    },
    marks() { attach(); return shown; },
  };
}
